import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useTracking } from '../hooks/useTracking';
import { TRACKING_EVENTS } from '../utils/trackingEvents';
import { EntityHistory } from './Analytics';
import { COMMON_STYLES } from '../utils/constants';

// Adresse du depot pour le lien Google Maps
const DEPOT_ADDRESS = 'ZA du Bos Plan, 3 Rte des Chevreuils, 33750 Beychac-et-Caillau';

// Styles locaux
const STYLES = {
  overlay: { ...COMMON_STYLES.MODAL_OVERLAY, background: 'rgba(0,0,0,0.5)', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '16px', padding: '20px', width: '95vw', maxWidth: '1100px', maxHeight: '95vh', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  headerTitle: { margin: 0, fontSize: '18px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' },
  errorBox: { ...COMMON_STYLES.ALERT, ...COMMON_STYLES.ALERT_DANGER, marginBottom: '12px', fontSize: '13px' },
  form: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
  mainLayout: { display: 'flex', gap: '20px', flex: 1, minHeight: 0 },
  formColumn: { flex: 1, minWidth: 0 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' },
  fullWidth: { gridColumn: '1 / -1' },
  span2: { gridColumn: 'span 2' },
  inputStyle: { width: '100%', padding: '8px 10px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  labelStyle: { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' },
  distanceRow: { display: 'flex', gap: '6px' },
  calcBtn: (calculating) => ({ padding: '8px 12px', background: calculating ? '#6c757d' : 'linear-gradient(135deg, #28a745, #20c997)', color: 'white', border: 'none', borderRadius: '8px', cursor: calculating ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }),
  statusRow: (color) => ({ marginTop: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px', color }),
  validateBtn: (validating) => ({ padding: '1px 6px', fontSize: '10px', background: validating ? '#6c757d' : '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: validating ? 'not-allowed' : 'pointer' }),
  discrepancyText: (color) => ({ color, marginTop: '2px' }),
  textareaStyle: { width: '100%', padding: '8px 10px', border: '2px solid #e1e5e9', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  checkbox: { width: '16px', height: '16px' },
  checkboxText: { fontWeight: '600', fontSize: '13px' },
  section: { marginTop: '10px', borderTop: '1px solid #e0e0e0', paddingTop: '8px' },
  worksheetLabel: { display: 'flex', alignItems: 'center', gap: '6px' },
  worksheetDate: { fontWeight: 'normal', fontSize: '11px', color: '#6c757d' },
  worksheetBox: { padding: '10px 12px', background: '#fff3cd', borderLeft: '4px solid #ffc107', borderRadius: '0 8px 8px 0', fontSize: '13px', lineHeight: '1.5', color: '#856404', whiteSpace: 'pre-wrap' },
  historyBtn: { background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#1976d2', fontWeight: 500 },
  historyContent: { marginTop: '10px' },
  notesRow: { gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  locationColumn: { width: '280px', flexShrink: 0, background: '#f8f9fa', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  locationTitle: { fontWeight: '600', fontSize: '14px', color: '#495057' },
  photoContainer: { width: '100%', aspectRatio: '1', position: 'relative' },
  photoLoading: { width: '100%', height: '100%', background: '#e9ecef', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d', fontSize: '12px' },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '2px solid #dee2e6' },
  photoPlaceholder: { width: '100%', height: '100%', background: '#e9ecef', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d', fontSize: '12px', textAlign: 'center', padding: '8px' },
  gpsInfo: { fontSize: '12px', color: '#6c757d' },
  gpsRow: { marginBottom: '4px' },
  distanceValue: { color: '#495057' },
  mapsLink: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', background: 'linear-gradient(135deg, #4285f4, #34a853)', color: 'white', textDecoration: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600' },
  footer: { display: 'flex', gap: '10px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' },
  cancelBtn: { flex: 1, padding: '10px', border: '2px solid #e1e5e9', borderRadius: '8px', background: 'white', cursor: 'pointer', fontWeight: '600' },
  submitBtn: (loading) => ({ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #17a2b8, #138496)', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600' })
};

const LocationForm = ({ token, location, onClose, onSave }) => {
  const { trackView } = useTracking();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    contact_name: '',
    contact_email: '',
    distance_from_depot: '',
    notes: '',
    is_active: true,
    latitude: '',
    longitude: '',
    geocoding_status: '',
    geocoding_discrepancy_m: null,
    geocoding_ign_label: '',
    geocoding_ors_label: ''
  });
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [calculationInfo, setCalculationInfo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [aerialPhoto, setAerialPhoto] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  useEffect(() => {
    if (location) {
      setFormData({
        code: location.code || '',
        name: location.name || '',
        address: location.address || '',
        postal_code: location.postal_code || '',
        city: location.city || '',
        phone: location.phone || '',
        contact_name: location.contact_name || '',
        contact_email: location.contact_email || '',
        distance_from_depot: location.distance_from_depot || '',
        notes: location.notes || '',
        is_active: location.is_active !== false,
        latitude: location.latitude || '',
        longitude: location.longitude || '',
        geocoding_status: location.geocoding_status || '',
        geocoding_discrepancy_m: location.geocoding_discrepancy_m || null,
        geocoding_ign_label: location.geocoding_ign_label || '',
        geocoding_ors_label: location.geocoding_ors_label || ''
      });

      // Tracker la consultation (audit)
      const entityLabel = `${location.name}${location.city ? ` (${location.city})` : ''}`;
      trackView(TRACKING_EVENTS.LOCATION_VIEWED, 'location', location.id, entityLabel);
    }
  }, [location, trackView]);

  // Charger la photo aerienne quand les coordonnees sont disponibles
  useEffect(() => {
    const loadAerialPhoto = async () => {
      const lat = formData.latitude;
      const lng = formData.longitude;
      if (!lat || !lng) {
        setAerialPhoto(null);
        return;
      }

      setLoadingPhoto(true);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.GEO}/aerial-photo?lat=${lat}&lon=${lng}&zoom=17`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const data = await response.json();
        if (data.success && data.data?.base64) {
          setAerialPhoto(data.data.base64);
        } else {
          setAerialPhoto(null);
        }
      } catch (err) {
        console.error('Erreur chargement photo aerienne:', err);
        setAerialPhoto(null);
      } finally {
        setLoadingPhoto(false);
      }
    };

    loadAerialPhoto();
  }, [formData.latitude, formData.longitude, token]);

  // Construire le lien Google Maps itineraire
  const buildMapsUrl = () => {
    if (!formData.latitude || !formData.longitude) return null;
    const destination = `${formData.latitude},${formData.longitude}`;
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(DEPOT_ADDRESS)}&destination=${destination}&travelmode=driving`;
  };

  // Calculer la distance depuis le dépôt
  const handleCalculateDistance = async () => {
    if (!formData.address && !formData.city) {
      setError('Veuillez saisir une adresse ou une ville');
      return;
    }

    setCalculating(true);
    setError('');
    setCalculationInfo(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.LOCATIONS}/calculate-distance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          address: formData.address,
          postal_code: formData.postal_code,
          city: formData.city
        })
      });

      const data = await response.json();

      if (data.success) {
        // Arrondir la distance à l'entier supérieur
        const distanceKm = Math.ceil(data.data.distance_km);
        const lat = data.data.latitude;
        const lon = data.data.longitude;

        console.log('Distance calculée:', data.data.distance_km, '-> arrondie à', distanceKm);

        // Mettre à jour le formulaire avec les nouvelles valeurs
        setFormData(prev => ({
          ...prev,
          distance_from_depot: distanceKm.toString(),
          latitude: lat != null ? lat.toString() : '',
          longitude: lon != null ? lon.toString() : '',
          geocoding_status: data.data.geocoding_status || 'pending',
          geocoding_discrepancy_m: data.data.geocoding_discrepancy_m,
          geocoding_ign_label: data.data.geocoding_ign_label || '',
          geocoding_ors_label: data.data.geocoding_ors_label || ''
        }));

        setCalculationInfo({
          distance: distanceKm,
          duration: data.data.duration_min,
          geocoding_status: data.data.geocoding_status,
          geocoding_discrepancy_m: data.data.geocoding_discrepancy_m,
          geocoding_ign_label: data.data.geocoding_ign_label,
          geocoding_ors_label: data.data.geocoding_ors_label
        });
      } else {
        setError(data.error || 'Erreur lors du calcul');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setCalculating(false);
    }
  };

  // Valider manuellement l'adresse
  const handleValidateAddress = async () => {
    if (!location?.id) return;

    setValidating(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.LOCATIONS}/${location.id}/validate-address`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          geocoding_status: 'ok'
        }));
        setCalculationInfo(prev => prev ? { ...prev, geocoding_status: 'ok' } : null);
      } else {
        setError(data.error || 'Erreur lors de la validation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = location
        ? API_ENDPOINTS.LOCATION_BY_ID(location.id)
        : API_ENDPOINTS.LOCATIONS;
      const method = location ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          distance_from_depot: formData.distance_from_depot ? parseInt(formData.distance_from_depot) : null,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null
        })
      });

      const data = await response.json();

      if (data.success) {
        onSave();
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Determiner si on a des coordonnees pour afficher le panneau lateral
  const hasLocation = formData.latitude && formData.longitude;

  // Helper pour couleur statut geocoding
  const getStatusColor = (status) => {
    if (status === 'ok') return '#28a745';
    if (status === 'warning') return '#856404';
    if (status === 'error') return '#dc3545';
    return '#6c757d';
  };

  return (
    <div style={STYLES.overlay}>
      <div style={STYLES.modal}>
        <div style={STYLES.header}>
          <h2 style={STYLES.headerTitle}>{location ? 'Modifier le lieu' : 'Nouveau lieu de mise'}</h2>
          <button onClick={onClose} style={STYLES.closeBtn}>X</button>
        </div>

        {error && <div style={STYLES.errorBox}>{error}</div>}

        <form onSubmit={handleSubmit} style={STYLES.form}>
          {/* Layout principal: Formulaire a gauche, Localisation a droite */}
          <div style={STYLES.mainLayout}>
            {/* Colonne gauche: Formulaire */}
            <div style={STYLES.formColumn}>
              <div style={STYLES.formGrid}>
                {/* Ligne 1: Code + Nom */}
                <div>
                  <label style={STYLES.labelStyle}>Code *</label>
                  <input type="text" name="code" value={formData.code} onChange={handleChange} required style={STYLES.inputStyle} placeholder="LOC-001" />
                </div>

                <div style={STYLES.span2}>
                  <label style={STYLES.labelStyle}>Nom *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required style={STYLES.inputStyle} placeholder="Nom du lieu" />
                </div>

                {/* Ligne 2: Adresse + Ville + CP */}
                <div>
                  <label style={STYLES.labelStyle}>Adresse</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} style={STYLES.inputStyle} placeholder="123 rue Example" />
                </div>

                <div>
                  <label style={STYLES.labelStyle}>Ville</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} style={STYLES.inputStyle} placeholder="Bordeaux" />
                </div>

                <div>
                  <label style={STYLES.labelStyle}>Code postal</label>
                  <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} style={STYLES.inputStyle} placeholder="33000" />
                </div>

                {/* Ligne 3: Contact sur site */}
                <div>
                  <label style={STYLES.labelStyle}>Contact</label>
                  <input type="text" name="contact_name" value={formData.contact_name} onChange={handleChange} style={STYLES.inputStyle} placeholder="Nom du contact" />
                </div>

                <div>
                  <label style={STYLES.labelStyle}>Téléphone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={STYLES.inputStyle} placeholder="05 56 00 00 00" />
                </div>

                <div>
                  <label style={STYLES.labelStyle}>Email</label>
                  <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} style={STYLES.inputStyle} placeholder="contact@example.com" />
                </div>

                {/* Ligne 5: Notes + Commentaires fiche chantier */}
                <div style={location?.worksheet_comments ? STYLES.notesRow : STYLES.fullWidth}>
                  <div>
                    <label style={STYLES.labelStyle}>Notes</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" style={STYLES.textareaStyle} placeholder="Notes sur ce lieu..." />
                  </div>
                  {location?.worksheet_comments && (
                    <div>
                      <label style={{ ...STYLES.labelStyle, ...STYLES.worksheetLabel }}>
                        <span>Commentaires fiche chantier</span>
                        {location.worksheet_comments_updated_at && (
                          <span style={STYLES.worksheetDate}>(MAJ: {new Date(location.worksheet_comments_updated_at).toLocaleDateString('fr-FR')})</span>
                        )}
                      </label>
                      <div style={{ ...STYLES.worksheetBox, minHeight: '60px' }}>{location.worksheet_comments}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Section Historique (mode edition uniquement) */}
              {location?.id && (
                <div style={STYLES.section}>
                  <button type="button" onClick={() => setShowHistory(!showHistory)} style={STYLES.historyBtn}>
                    <span>{showHistory ? '▼' : '▶'}</span>
                    <span>Historique des modifications</span>
                  </button>
                  {showHistory && (
                    <div style={STYLES.historyContent}>
                      <EntityHistory entityType="location" entityId={location.id} token={token} limit={20} showHeader={false} compact={true} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Colonne droite: Localisation */}
            <div style={STYLES.locationColumn}>
              {/* Checkbox Lieu actif en haut */}
              <label style={STYLES.checkboxLabel}>
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} style={STYLES.checkbox} />
                <span style={STYLES.checkboxText}>Lieu actif</span>
              </label>

              <div style={STYLES.locationTitle}>Localisation</div>

              {/* Photo aerienne */}
              <div style={STYLES.photoContainer}>
                {loadingPhoto ? (
                  <div style={STYLES.photoLoading}>Chargement...</div>
                ) : aerialPhoto ? (
                  <img src={`data:image/png;base64,${aerialPhoto}`} alt="Vue aerienne" style={STYLES.photoImg} />
                ) : (
                  <div style={STYLES.photoPlaceholder}>Photo non disponible</div>
                )}
              </div>

              {/* Infos GPS */}
              {hasLocation && (
                <div style={STYLES.gpsInfo}>
                  <div style={STYLES.gpsRow}>GPS: {parseFloat(formData.latitude).toFixed(5)}, {parseFloat(formData.longitude).toFixed(5)}</div>
                </div>
              )}

              {/* Bouton Google Maps */}
              {hasLocation && (
                <a href={buildMapsUrl()} target="_blank" rel="noopener noreferrer" style={STYLES.mapsLink}>
                  <span>Itineraire Google Maps</span>
                </a>
              )}

              {/* Distance + Calculer sous l'itineraire */}
              <div>
                <label style={STYLES.labelStyle}>Distance depot (km)</label>
                <div style={STYLES.distanceRow}>
                  <input type="number" name="distance_from_depot" value={formData.distance_from_depot} onChange={handleChange}
                    step="1" min="0" style={{ ...STYLES.inputStyle, flex: 1 }} placeholder="0" />
                  <button type="button" onClick={handleCalculateDistance} disabled={calculating || (!formData.address && !formData.city)}
                    style={STYLES.calcBtn(calculating)} title="Calculer la distance depuis le depot">
                    {calculating ? '...' : 'Calculer'}
                  </button>
                </div>
                {/* Afficher le statut existant si pas de nouveau calcul */}
                {!calculationInfo && formData.geocoding_status && formData.distance_from_depot && (
                  <div style={STYLES.statusRow(getStatusColor(formData.geocoding_status))}>
                    {formData.geocoding_status === 'ok' && <span>✓ Validee</span>}
                    {formData.geocoding_status === 'warning' && <span>⚠ A verifier ({formData.geocoding_discrepancy_m}m)</span>}
                    {formData.geocoding_status === 'error' && <span>✗ Invalide ({formData.geocoding_discrepancy_m}m)</span>}
                    {formData.geocoding_status === 'pending' && <span>⏳ En attente</span>}
                    {(formData.geocoding_status === 'warning' || formData.geocoding_status === 'error') && location?.id && (
                      <button type="button" onClick={handleValidateAddress} disabled={validating} style={STYLES.validateBtn(validating)}>
                        {validating ? '...' : '✓'}
                      </button>
                    )}
                  </div>
                )}
                {/* Afficher le résultat du calcul */}
                {calculationInfo && (
                  <div style={{ marginTop: '4px', fontSize: '11px' }}>
                    <div style={STYLES.statusRow(getStatusColor(calculationInfo.geocoding_status) || '#28a745')}>
                      {calculationInfo.geocoding_status === 'ok' && <span>✓</span>}
                      {calculationInfo.geocoding_status === 'warning' && <span>⚠</span>}
                      {calculationInfo.geocoding_status === 'error' && <span>✗</span>}
                      {!calculationInfo.geocoding_status && <span>✓</span>}
                      <span>{calculationInfo.distance} km • ~{calculationInfo.duration} min</span>
                      {(calculationInfo.geocoding_status === 'warning' || calculationInfo.geocoding_status === 'error') && location?.id && (
                        <button type="button" onClick={handleValidateAddress} disabled={validating} style={STYLES.validateBtn(validating)}>
                          {validating ? '...' : '✓'}
                        </button>
                      )}
                    </div>
                    {(calculationInfo.geocoding_status === 'warning' || calculationInfo.geocoding_status === 'error') && (
                      <div style={STYLES.discrepancyText(getStatusColor(calculationInfo.geocoding_status))}>
                        Ecart {calculationInfo.geocoding_discrepancy_m}m
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Boutons action */}
          <div style={STYLES.footer}>
            <button type="button" onClick={onClose} style={STYLES.cancelBtn}>Annuler</button>
            <button type="submit" disabled={loading} style={STYLES.submitBtn(loading)}>
              {loading ? 'Enregistrement...' : (location ? 'Modifier' : 'Creer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
